import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams as useUrlSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  FolderZip as ZipIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { startOfMonth, endOfMonth, subMonths, startOfYear } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as locales from 'date-fns/locale';
import axios from 'axios';
import i18next, { getBrowserLanguage } from '../../lang';
import { rootStore } from "../../stores/RootStore";
import { adminStyles } from '../../theme/adminStyles';

// 階層レスポンス型定義
interface ClassroomInvoice {
  classroomUserId: string;
  classroomName: string;
  classroomInvoiceId: number;
  classroomInvoiceNumber: string | null;
  classroomAmount: number;
}

interface BrandGroup {
  brandUserId: string;
  brandName: string;
  brandInvoiceId: number | null;
  brandInvoiceNumber: string | null;
  brandAmount: number | null;
  brandHasOwnOrders: boolean;
  classrooms: ClassroomInvoice[];
}

interface InvoiceGroup {
  corpUserId: string;
  corpName: string;
  invoiceUnit: string | null;
  dueDate: string;
  createdAt: string;
  corpInvoiceId: number;
  corpInvoiceNumber: string | null;
  corpAmount: number;
  brands: BrandGroup[];
}

interface InvoiceHierarchyResponse {
  groups: InvoiceGroup[];
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatAmount = (amount: number | null) => {
  if (amount == null) return '';
  return `¥${amount.toLocaleString()}`;
};


const downloadInvoice = async (invoiceId: number, format: 'pdf' | 'xlsx', excludeSubordinates?: boolean) => {
  try {
    const params: any = { format };
    if (excludeSubordinates) params.excludeSubordinates = true;
    const response = await axios.get(`/api/v1/invoices/${invoiceId}/download`, {
      params,
      responseType: 'blob',
    });
    const mimeType = format === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/pdf';
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice_${invoiceId}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

const downloadZip = async (invoiceId: number) => {
  try {
    const response = await axios.get(`/api/v1/invoices/${invoiceId}/download-zip`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices_${invoiceId}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('ZIP download failed:', error);
  }
};

const InvoiceManagement: React.FC = () => {
  const { authStore, viewStatusStore } = rootStore;
  const isAdmin = authStore.user?.level === 'ADMIN';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // URL クエリと同期 (戻るボタンで検索状態を復元するため)
  const [urlParams, setUrlParams] = useUrlSearchParams();
  const parseDate = (s: string | null) => s ? new Date(s) : null;
  const [searchParams, setSearchParams] = useState({
    startDate: parseDate(urlParams.get('startDate')),
    endDate: parseDate(urlParams.get('endDate')),
  });
  const [groups, setGroups] = useState<InvoiceGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCorps, setExpandedCorps] = useState<Set<string>>(new Set());
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());
  const [activePreset, setActivePreset] = useState<string | null>(urlParams.get('preset') || null);
  const [presetMonth, setPresetMonth] = useState<Date | null>(parseDate(urlParams.get('presetMonth')));

  const syncToUrl = useCallback((overrides?: {
    startDate?: Date | null;
    endDate?: Date | null;
    preset?: string | null;
    presetMonth?: Date | null;
  }) => {
    const next = new URLSearchParams();
    const startDate = overrides?.startDate !== undefined ? overrides.startDate : searchParams.startDate;
    const endDate = overrides?.endDate !== undefined ? overrides.endDate : searchParams.endDate;
    const preset = overrides?.preset !== undefined ? overrides.preset : activePreset;
    const pMonth = overrides?.presetMonth !== undefined ? overrides.presetMonth : presetMonth;

    if (startDate) next.set('startDate', startDate.toLocaleDateString('sv').split(' ')[0]);
    if (endDate) next.set('endDate', endDate.toLocaleDateString('sv').split(' ')[0]);
    if (preset) next.set('preset', preset);
    if (pMonth) next.set('presetMonth', pMonth.toLocaleDateString('sv').split(' ')[0]);

    setUrlParams(next, { replace: true });
  }, [searchParams.startDate, searchParams.endDate, activePreset, presetMonth, setUrlParams]);
  const [manualGenerateDialogOpen, setManualGenerateDialogOpen] = useState(false);
  const [manualGenerateLoading, setManualGenerateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // 納品書CSV出力
  const [deliveryCsvDialogOpen, setDeliveryCsvDialogOpen] = useState(false);
  const [csvStartDate, setCsvStartDate] = useState<Date | null>(null);
  const [csvEndDate, setCsvEndDate] = useState<Date | null>(null);
  const [csvCorpId, setCsvCorpId] = useState('');
  const [corpPartners, setCorpPartners] = useState<{id: string, name: string}[]>([]);

  const fetchHierarchy = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.startDate) {
        params.append('startDate', searchParams.startDate.toLocaleDateString('sv').split(' ')[0]);
      }
      if (searchParams.endDate) {
        params.append('endDate', searchParams.endDate.toLocaleDateString('sv').split(' ')[0]);
      }
      const response = await axios.get<InvoiceHierarchyResponse>('/api/v1/invoices/hierarchy', { params });
      setGroups(response.data.groups);
    } catch (error) {
      console.error('Failed to fetch invoice hierarchy:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams.startDate, searchParams.endDate]);

  const handleManualGenerate = useCallback(async () => {
    setManualGenerateLoading(true);
    try {
      const today = new Date().toLocaleDateString('sv').split(' ')[0]; // YYYY-MM-DD
      await axios.post('/api/v1/invoices', today, {
        headers: { 'Content-Type': 'text/plain' },
      });
      setSnackbar({ open: true, message: i18next.t('invoice.manual_generate_success'), severity: 'success' });
      fetchHierarchy();
    } catch (error) {
      console.error('Manual invoice generation failed:', error);
      setSnackbar({ open: true, message: i18next.t('invoice.manual_generate_failed'), severity: 'error' });
    } finally {
      setManualGenerateLoading(false);
      setManualGenerateDialogOpen(false);
    }
  }, [fetchHierarchy]);

  // 納品書CSVダウンロード
  const handleDeliveryCsvDownload = async () => {
    if (!csvStartDate || !csvEndDate || !csvCorpId) return;
    try {
      viewStatusStore.setLoadingStatus({ open: true });
      // toISOString() は UTC ベースで日付がずれるため、ローカル日付を直接構築する
      const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const params = new URLSearchParams({
        startDate: fmtDate(csvStartDate),
        endDate: fmtDate(csvEndDate),
        corpId: csvCorpId
      });
      const response = await axios.get(`/api/v1/invoices/delivery-csv?${params}`);
      const rows: any[] = response.data;

      if (rows.length === 0) {
        alert('対象データがありません');
        return;
      }

      const headers = [
        '行形式','発行日','番号','枝番','件名','発行元担当者氏名','社内メモ','備考',
        '消費税の表示方法','消費税端数の計算方法','取引先名称','取引先宛名','取引先敬称',
        '取引先郵便番号','取引先都道府県','取引先市区町村・番地','取引先建物名・部屋番号',
        '取引先部署','取引先担当者氏名','行の種類','摘要','単価','数量','単位','税率','源泉徴収'
      ];

      const fieldKeys = [
        'rowFormat','issueDate','number','subNumber','subject','issuerName','internalMemo','note',
        'taxDisplay','taxRounding','clientName','clientAddress','clientTitle','clientZip',
        'clientPrefecture','clientCity','clientBuilding','clientDepartment','clientContactName',
        'lineType','description','unitPrice','quantity','unit','taxRate','withholdingTax'
      ];

      const csvContent = [
        headers.join(','),
        ...rows.map(row => fieldKeys.map(key => {
          const val = row[key] ?? '';
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const corpName = corpPartners.find(c => c.id === csvCorpId)?.name || '';
      a.download = `納品書_${corpName}_${fmtDate(csvStartDate)}_${fmtDate(csvEndDate)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setDeliveryCsvDialogOpen(false);
    } catch (error) {
      console.error('CSV download failed:', error);
      alert('CSV出力に失敗しました');
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  // Admin: 法人一覧を取得（納品書CSV出力の法人セレクト用）
  useEffect(() => {
    if (isAdmin) {
      axios.get('/api/v1/admin/users?level=CORP_PARTNER&rowsPerPage=100&page=0')
        .then(res => setCorpPartners(
          (res.data.users || []).map((u: any) => ({ id: u.id, name: u.organizationName }))
        ));
    }
  }, [isAdmin]);

  const handleSearch = () => {
    setActivePreset(null);
    setPresetMonth(null);
    syncToUrl({ preset: null, presetMonth: null });
    fetchHierarchy();
  };

  const handlePreset = (preset: string) => {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = null;
    if (preset === 'this_month') {
      start = startOfMonth(now);
      end = now;
    } else if (preset === 'last_month') {
      const lastMonth = subMonths(now, 1);
      start = startOfMonth(lastMonth);
      end = endOfMonth(lastMonth);
    } else if (preset === 'this_year') {
      start = startOfYear(now);
      end = now;
    } else {
      start = null;
      end = null;
    }
    setPresetMonth(null);
    setActivePreset(preset);
    setSearchParams({ startDate: start, endDate: end });
    syncToUrl({ startDate: start, endDate: end, preset, presetMonth: null });
  };

  const handleMonthPreset = (date: Date | null) => {
    if (date) {
      setPresetMonth(date);
      setActivePreset('month');
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      setSearchParams({ startDate: start, endDate: end });
      syncToUrl({ startDate: start, endDate: end, preset: 'month', presetMonth: date });
    }
  };

  // プリセット選択後にfetchHierarchyを呼ぶ
  useEffect(() => {
    if (activePreset !== null) {
      fetchHierarchy();
    }
  }, [activePreset, fetchHierarchy]);

  const toggleCorp = (key: string) => {
    setExpandedCorps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleBrand = (key: string) => {
    setExpandedBrands(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const corpKeys = new Set<string>();
    const brandKeys = new Set<string>();
    for (const group of groups) {
      if (group.brands && group.brands.length > 0) {
        corpKeys.add(`${group.corpUserId}_${group.dueDate}`);
        for (const brand of group.brands) {
          if (brand.classrooms && brand.classrooms.length > 0) {
            brandKeys.add(`${brand.brandUserId}_${group.dueDate}`);
          }
        }
      }
    }
    setExpandedCorps(corpKeys);
    setExpandedBrands(brandKeys);
  };

  const collapseAll = () => {
    setExpandedCorps(new Set());
    setExpandedBrands(new Set());
  };

  // ダウンロードボタン
  const DownloadButtons: React.FC<{ invoiceId: number; showZip?: boolean; showExcel?: boolean; showPdf?: boolean; excludeSubordinates?: boolean }> = ({ invoiceId, showZip, showExcel = true, showPdf = true, excludeSubordinates }) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
      {showPdf && (
        <Button
          size="small"
          startIcon={<PdfIcon sx={{ m: 0, p: 0 }} />}
          onClick={(e) => { e.stopPropagation(); downloadInvoice(invoiceId, 'pdf', excludeSubordinates); }}
          sx={{
            fontSize: '0.7rem', minWidth: 'unset', p: '2px 6px', m: 0,
            '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 }
          }}
        >
          PDF
        </Button>
      )}
      {isAdmin && showExcel && (
        <Button
          size="small"
          startIcon={<ExcelIcon sx={{ m: 0, p: 0 }} />}
          onClick={(e) => { e.stopPropagation(); downloadInvoice(invoiceId, 'xlsx', excludeSubordinates); }}
          sx={{
            fontSize: '0.7rem', minWidth: 'unset', p: '2px 6px', m: 0, color: 'green',
            '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 }
          }}
        >
          Excel
        </Button>
      )}
      {showZip && (
        <Button
          size="small"
          startIcon={<ZipIcon sx={{ m: 0, p: 0 }} />}
          onClick={(e) => { e.stopPropagation(); downloadZip(invoiceId); }}
          sx={{
            fontSize: '0.7rem', minWidth: 'unset', p: '2px 6px', m: 0, color: '#ff9800',
            '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 }
          }}
        >
          ZIP
        </Button>
      )}
    </Box>
  );

  // 行をフラットにレンダリング（UserListViewと同じパターン）
  const renderRows = () => {
    const rows: React.ReactNode[] = [];

    for (const group of groups) {
      const corpKey = `${group.corpUserId}_${group.dueDate}`;
      const isCorpExpanded = expandedCorps.has(corpKey);
      const hasChildren = group.brands && group.brands.length > 0;
      // 法人行: 配下がある場合は発行単位ではないのでIDなし・ZIPのみ
      rows.push(
        <TableRow
          key={corpKey}
          hover
          sx={{
            backgroundColor: 'grey.50',
            cursor: hasChildren ? 'pointer' : 'default',
          }}
          onClick={() => hasChildren && toggleCorp(corpKey)}
        >
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {!hasChildren ? `#${group.corpInvoiceId}` : ''}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {!hasChildren ? group.corpInvoiceNumber || '' : ''}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {hasChildren ? (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCorp(corpKey); }} sx={{ mr: 0.5, p: '2px' }}>
                  {isCorpExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              ) : (
                <Box sx={{ width: 28, mr: 0.5 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {group.corpName}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatAmount(group.corpAmount)}</TableCell>
          <TableCell align="right">
            {/* 法人で配下なし→請求金額=法人金額、配下あり→空 */}
            {!hasChildren ? formatAmount(group.corpAmount) : ''}
          </TableCell>
          <TableCell align="center">{formatDate(group.dueDate)}</TableCell>
          <TableCell align="center">{formatDate(group.createdAt)}</TableCell>
          <TableCell align="right">
            <DownloadButtons invoiceId={group.corpInvoiceId} showZip={hasChildren} />
          </TableCell>
        </TableRow>
      );

      // ブランド行（展開時のみ）
      if (hasChildren && isCorpExpanded) {
        for (const brand of group.brands) {
          const brandKey = `${brand.brandUserId}_${group.dueDate}`;
          const isBrandExpanded = expandedBrands.has(brandKey);
          const hasBrandChildren = brand.classrooms && brand.classrooms.length > 0;

          rows.push(
            <TableRow
              key={brandKey}
              hover
              sx={{
                cursor: hasBrandChildren ? 'pointer' : 'default',
                bgcolor: 'rgba(0,0,0,0.02)',
              }}
              onClick={() => hasBrandChildren && toggleBrand(brandKey)}
            >
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {/* ブランド自身の注文がある場合のみID表示 */}
                {brand.brandInvoiceId != null && brand.brandHasOwnOrders ? `#${brand.brandInvoiceId}` : ''}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {brand.brandInvoiceId != null && brand.brandHasOwnOrders ? brand.brandInvoiceNumber || '' : ''}
              </TableCell>
              <TableCell sx={{ pl: `${16 + 20}px`, whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasBrandChildren ? (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleBrand(brandKey); }} sx={{ mr: 0.5, p: '2px' }}>
                      {isBrandExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 28, mr: 0.5 }} />
                  )}
                  <Typography variant="body2">
                    {brand.brandName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell />
              <TableCell align="right">
                {brand.brandAmount != null ? formatAmount(brand.brandAmount) : ''}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right">
                {brand.brandInvoiceId != null && (
                  <DownloadButtons invoiceId={brand.brandInvoiceId} showZip={hasBrandChildren} />
                )}
              </TableCell>
            </TableRow>
          );

          // 教場行（展開時のみ）
          if (hasBrandChildren && isBrandExpanded) {
            for (const classroom of brand.classrooms) {
              rows.push(
                <TableRow
                  key={`classroom_${classroom.classroomInvoiceId}`}
                  hover
                  sx={{ bgcolor: 'rgba(0,0,0,0.04)' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    #{classroom.classroomInvoiceId}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {classroom.classroomInvoiceNumber || ''}
                  </TableCell>
                  <TableCell sx={{ pl: `${16 + 40}px`, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 28, mr: 0.5 }} />
                      <Typography variant="body2">
                        {classroom.classroomName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">{formatAmount(classroom.classroomAmount)}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">
                    <DownloadButtons invoiceId={classroom.classroomInvoiceId} />
                  </TableCell>
                </TableRow>
              );
            }
          }
        }
      }
    }

    return rows;
  };

  // モバイル: 請求書情報行の共通スタイル
  const infoRowSx = { display: 'flex', justifyContent: 'space-between', py: '2px', borderBottom: '1px solid', borderColor: 'divider' };

  // モバイル: 教場カード
  const renderClassroomCard = (classroom: ClassroomInvoice, isLast: boolean) => {
    const connectorColor = '#bbb';
    return (
      <Box key={`classroom_${classroom.classroomInvoiceId}`} sx={{ display: 'flex' }}>
        <Box sx={{
          width: 24, flexShrink: 0, position: 'relative',
          '&::before': { content: '""', position: 'absolute', left: '12px', top: 0, height: isLast ? '24px' : '100%', borderLeft: `2px solid ${connectorColor}` },
          '&::after': { content: '""', position: 'absolute', left: '12px', top: '24px', width: '12px', borderTop: `2px solid ${connectorColor}` },
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper elevation={0} sx={{ mb: 1, p: 1.5, mt: '4px', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{classroom.classroomName}</Typography>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              <Box sx={infoRowSx}>
                <span>{i18next.t('invoice.billing_amount')}</span>
                <span style={{ fontWeight: 600 }}>{formatAmount(classroom.classroomAmount)}</span>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <DownloadButtons invoiceId={classroom.classroomInvoiceId} />
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  // モバイル: ブランドカード
  const renderBrandCard = (brand: BrandGroup, group: InvoiceGroup, isLast: boolean) => {
    const brandKey = `${brand.brandUserId}_${group.dueDate}`;
    const isBrandExpanded = expandedBrands.has(brandKey);
    const hasBrandChildren = brand.classrooms && brand.classrooms.length > 0;
    const connectorColor = '#bbb';

    return (
      <Box key={brandKey} sx={{ display: 'flex' }}>
        <Box sx={{
          width: 24, flexShrink: 0, position: 'relative',
          '&::before': { content: '""', position: 'absolute', left: '12px', top: 0, height: isLast ? '24px' : '100%', borderLeft: `2px solid ${connectorColor}` },
          '&::after': { content: '""', position: 'absolute', left: '12px', top: '24px', width: '12px', borderTop: `2px solid ${connectorColor}` },
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              mb: 1, p: 1.5, mt: '4px',
              border: '1px solid', borderColor: 'info.light', borderRadius: '8px',
              cursor: hasBrandChildren ? 'pointer' : 'default',
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
            onClick={() => hasBrandChildren && toggleBrand(brandKey)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              {hasBrandChildren && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleBrand(brandKey); }} sx={{ mr: 0.5, p: '2px' }}>
                  {isBrandExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              )}
              <Typography variant="body1" sx={{ flex: 1 }}>{brand.brandName}</Typography>
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {brand.brandAmount != null && (
                <Box sx={infoRowSx}>
                  <span>{i18next.t('invoice.billing_amount')}</span>
                  <span style={{ fontWeight: 600 }}>{formatAmount(brand.brandAmount)}</span>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              {brand.brandInvoiceId != null && (
                <DownloadButtons invoiceId={brand.brandInvoiceId} showZip={hasBrandChildren} />
              )}
            </Box>
          </Paper>
          {/* 教場配下 */}
          {isBrandExpanded && hasBrandChildren && brand.classrooms.map((classroom, idx) =>
            renderClassroomCard(classroom, idx === brand.classrooms.length - 1)
          )}
        </Box>
      </Box>
    );
  };

  // モバイルカードビュー
  const renderMobileCards = () => (
    <Box sx={{ p: 1 }}>
      {groups.map((group) => {
        const corpKey = `${group.corpUserId}_${group.dueDate}`;
        const isCorpExpanded = expandedCorps.has(corpKey);
        const hasChildren = group.brands && group.brands.length > 0;

        return (
          <Box key={corpKey}>
            <Paper
              elevation={0}
              sx={{
                mb: 1, p: 1.5,
                border: '1px solid', borderColor: 'primary.light', borderRadius: '8px',
                cursor: hasChildren ? 'pointer' : 'default',
                backgroundColor: '#fff',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => hasChildren && toggleCorp(corpKey)}
            >
              {/* 法人ヘッダー */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {hasChildren && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCorp(corpKey); }} sx={{ mr: 0.5, p: '2px' }}>
                    {isCorpExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                )}
                <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>{group.corpName}</Typography>
              </Box>
              {/* 法人情報 */}
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', '& > div': infoRowSx }}>
                <Box>
                  <span>{i18next.t('invoice.total_amount')}</span>
                  <span style={{ fontWeight: 600 }}>{formatAmount(group.corpAmount)}</span>
                </Box>
                {!hasChildren && (
                  <Box>
                    <span>{i18next.t('invoice.billing_amount')}</span>
                    <span>{formatAmount(group.corpAmount)}</span>
                  </Box>
                )}
                <Box>
                  <span>{i18next.t('user.due_date')}</span>
                  <span>{formatDate(group.dueDate)}</span>
                </Box>
                <Box>
                  <span>{i18next.t('invoice.created_date')}</span>
                  <span>{formatDate(group.createdAt)}</span>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <DownloadButtons invoiceId={group.corpInvoiceId} showZip={hasChildren} />
              </Box>
            </Paper>
            {/* ブランド配下 */}
            {isCorpExpanded && hasChildren && group.brands.map((brand, idx) =>
              renderBrandCard(brand, group, idx === group.brands.length - 1)
            )}
          </Box>
        );
      })}
      {groups.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {i18next.t('invoice.no_invoices')}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* 検索・フィルター */}
      <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.filterPaper as any, ...(isMobile && { p: 1.5, mb: 1.5, borderRadius: 0, boxShadow: 'none', border: 'none' }) }}>
        <Stack
          direction="row"
          spacing={isMobile ? 1 : 2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 1 }}
        >
          <Stack direction="row" spacing={isMobile ? 1 : 2} sx={{ flex: isMobile ? 1 : 'none' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any} localeText={{ clearButtonLabel: i18next.t('common.cancel') }}>
              <DatePicker
                label={i18next.t('invoice.start_date')}
                value={searchParams.startDate}
                maxDate={searchParams.endDate || undefined}
                onChange={(newValue) => { setActivePreset(null); setPresetMonth(null); setSearchParams({ ...searchParams, startDate: newValue }); }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: isMobile,
                    sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }
                  },
                  actionBar: { actions: ['clear'] }
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any} localeText={{ clearButtonLabel: i18next.t('common.cancel') }}>
              <DatePicker
                label={i18next.t('invoice.end_date')}
                value={searchParams.endDate}
                minDate={searchParams.startDate || undefined}
                onChange={(newValue) => { setActivePreset(null); setPresetMonth(null); setSearchParams({ ...searchParams, endDate: newValue }); }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: isMobile,
                    sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }
                  },
                  actionBar: { actions: ['clear'] }
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              sx={{ ...adminStyles.searchButton, flexShrink: 0, fontSize: '0.8rem', height: 32, ...(isMobile && { minWidth: 32, px: 0.5 }) }}
            >
              {isMobile ? <SearchIcon sx={{ fontSize: '1.1rem' }} /> : i18next.t('common.search')}
            </Button>
          </Stack>
          {isAdmin && (
            <Stack direction="row" spacing={isMobile ? 1 : 2} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={() => setManualGenerateDialogOpen(true)}
                sx={{ flexShrink: 0, fontSize: '0.8rem', height: 32, whiteSpace: 'nowrap' }}
              >
                {i18next.t('invoice.manual_generate')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => setDeliveryCsvDialogOpen(true)}
                sx={{ flexShrink: 0, fontSize: '0.8rem', height: 32, whiteSpace: 'nowrap' }}
              >
                納品書CSV出力
              </Button>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any} localeText={{ clearButtonLabel: i18next.t('common.cancel') }}>
              <DatePicker
                views={['year', 'month']}
                format="yyyy/MM"
                value={presetMonth}
                minDate={new Date(2026, 0, 1)}
                maxDate={new Date()}
                onChange={handleMonthPreset}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: i18next.t('invoice.preset_select_month'),
                    sx: {
                      width: 150,
                      '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                    }
                  },
                  actionBar: { actions: ['clear'] }
                }}
              />
            </LocalizationProvider>
            {[
              { key: 'this_month', label: i18next.t('invoice.preset_this_month') },
              { key: 'last_month', label: i18next.t('invoice.preset_last_month') },
              { key: 'this_year', label: i18next.t('invoice.preset_this_year') },
              { key: 'all', label: i18next.t('invoice.preset_all') },
            ].map((preset) => (
              <Button
                key={preset.key}
                size="small"
                variant={activePreset === preset.key ? 'contained' : 'outlined'}
                onClick={() => handlePreset(preset.key)}
                sx={{ ...adminStyles.searchButton, fontSize: '0.8rem', height: 32, minWidth: 'unset', px: 1.5, whiteSpace: 'nowrap',
                  ...(activePreset !== preset.key && { backgroundColor: 'transparent', color: 'text.primary', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' } })
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, ml: 1 }}>
            <Tooltip title={i18next.t('product_mapping.expand_all')}>
              <Button
                variant="outlined"
                size="small"
                onClick={expandAll}
                sx={{ minWidth: 'unset', px: 0.5 }}
              >
                <UnfoldMoreIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title={i18next.t('product_mapping.collapse_all')}>
              <Button
                variant="outlined"
                size="small"
                onClick={collapseAll}
                sx={{ minWidth: 'unset', px: 0.5 }}
              >
                <UnfoldLessIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* 請求書階層テーブル / モバイルカード */}
      <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.tablePaper as any, ...(isMobile && { borderRadius: 0, boxShadow: 'none', border: 'none', overflow: 'visible' }) }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          renderMobileCards()
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {i18next.t('invoice.no_invoices')}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('invoice.serial_number')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('invoice.invoice_number')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('user.username')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">{i18next.t('invoice.total_amount')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">{i18next.t('invoice.billing_amount')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">{i18next.t('user.due_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">{i18next.t('invoice.created_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {renderRows()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* 手動集計確認ダイアログ */}
      <Dialog
        open={manualGenerateDialogOpen}
        onClose={() => !manualGenerateLoading && setManualGenerateDialogOpen(false)}
      >
        <DialogTitle>{i18next.t('invoice.manual_generate')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18next.t('invoice.manual_generate_confirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualGenerateDialogOpen(false)} disabled={manualGenerateLoading}>
            {i18next.t('common.cancel')}
          </Button>
          <Button
            onClick={handleManualGenerate}
            variant="contained"
            color="warning"
            disabled={manualGenerateLoading}
            startIcon={manualGenerateLoading ? <CircularProgress size={16} /> : undefined}
          >
            {i18next.t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 納品書CSV出力ダイアログ */}
      <Dialog open={deliveryCsvDialogOpen} onClose={() => setDeliveryCsvDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>納品書CSV出力</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
            <DatePicker
              label="開始日"
              value={csvStartDate}
              onChange={(v) => setCsvStartDate(v)}
              slotProps={{ textField: { fullWidth: true, margin: 'normal', size: 'small' } }}
            />
            <DatePicker
              label="終了日"
              value={csvEndDate}
              onChange={(v) => setCsvEndDate(v)}
              slotProps={{ textField: { fullWidth: true, margin: 'normal', size: 'small' } }}
            />
          </LocalizationProvider>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>法人</InputLabel>
            <Select value={csvCorpId} label="法人" onChange={(e) => setCsvCorpId(e.target.value)}>
              {corpPartners.map(cp => (
                <MenuItem key={cp.id} value={cp.id}>{cp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryCsvDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeliveryCsvDownload}
            disabled={!csvStartDate || !csvEndDate || !csvCorpId}
          >
            出力
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InvoiceManagement;
